// config-editor-vm.ts — PURE decision surfaces for THE configuration editor
// (Helm settings re-implementation, owner ruling 2026-09-08).
//
// One editor, two consumers: the Bridge's config-template editor
// (helm-console/src/client/config-templates.ts) and the Helm's SETTINGS tab
// (helm-bridge/src/client/settings-tab.ts) render the same component —
// config-editor.ts in this directory — over a schema shaped like the Bridge's
// config catalog. These functions are the judgments that component makes:
// what a row shows, whether it is changed, what SAVE will send, how a raw
// string parses, which stepper arrow is live, which section owns the
// viewport's nav row. No DOM, no fetch.
//
// HISTORY: every function here moved verbatim from
// helm-console/src/client/config-templates-vm.ts, which re-exports them so the
// console's headless drill (config-templates-ui.test.mjs) keeps bundling one
// module and measuring the same behaviour. The ONE seam added on the way is
// `pathFor`: the console's audit paths read `values.<section>.<key>` and the
// Helm has no audit trail with that shape, so the path is a parameter with the
// console's spelling as its default rather than a literal.

export type VarType = 'toggle' | 'number' | 'text' | 'select' | 'json';

/** One setting. Mirrors the Bridge's server catalog (config-catalog.ts
 *  CatalogVariable) — the Helm builds the same shape from its own state. */
export interface CfgVariable {
  key: string;
  label: string;
  type: VarType;
  default: unknown;
  /** Where the value lands (a file, a seat, an endpoint). Search text. */
  seat: string;
  /** Dotted path within the seat; '' = the whole document. */
  path: string;
  scope: string;
  help: string;
  min?: number;
  max?: number;
  step?: number;
  options?: { value: string; label: string }[];
}

export interface CfgSection {
  id: string;
  title: string;
  blurb: string;
  variables: CfgVariable[];
  /** Whether a value set here reaches a RELEASE VM (server-computed from
   * seat-delivery.ts). 'console-only' sections edit a seat no launch lane
   * carries — the template stores the value and the VM never reads it, so
   * the editor says so rather than letting a fleet reveal it. Optional so an
   * older server (no `reach` key) renders exactly as before. */
  reach?: 'release-vm' | 'console-only';
  /** What the LAUNCH preflight's `template.consumed` check does with a
   * non-default value in a `console-only` section ON THIS CONSOLE — the
   * preflight's own status vocabulary, server-computed from the SAME fact
   * it judges on (`cfg.releaseVm` present = this console holds a release-VM
   * seat; helm-console/src/server/template-preflight.ts:320-321, :428).
   * 'FAIL' = the preflight refuses the launch; 'WARN' = this console holds
   * no release-VM seat, so nothing launches from it and the preflight says
   * so instead of refusing. Optional, and the label asserts NO severity
   * when it is absent — an older server (or a consumer that builds this
   * shape from its own state) renders exactly as before, rather than the
   * editor asserting an enforcement it cannot see. */
  launchSeverity?: 'FAIL' | 'WARN';
  /** A section whose card body is NOT a row list — a management surface
   * (the Helm's accounts roster, its profiles CRUD) that still wants the
   * editor's card head, nav row, scroll-spy and search. The mount calls it
   * once with the card as host; the return is the teardown. Such a section
   * usually declares no variables. */
  render?: (host: HTMLElement) => (() => void) | void;
}

/** The one-line honesty label under a section's blurb.
 *
 * DERIVED, never asserted (#3346). The old second sentence read "The launch
 * preflight refuses a non-default value HERE" — and "here" is the EDITOR,
 * which refuses nothing: every `console-only` hit across the three client
 * trees is a CSS colour class. The refusal is LAUNCH-time, and its severity
 * is a property of THIS CONSOLE (whether it holds a release-VM seat), not of
 * the section. So the sentence is now built from `launchSeverity`, which the
 * server derives from the same fact the preflight judges on, and no severity
 * is claimed at all when the server did not send one.
 *
 * The cost of the old wording: it sent #3226's planner hunting for an
 * editor-side `reach !== 'release-vm'` refusal, which it correctly reported
 * NOT FOUND, with controls — a measurement dispatched after a mechanism
 * nobody ever wrote. */
export function reachNote(section: CfgSection): string | null {
  if (section.reach === 'console-only') {
    const base =
      'Does not reach a release VM — no launch lane carries this section\'s seats, so a release runs the framework default.';
    if (section.launchSeverity === 'FAIL') {
      return `${base} The launch preflight REFUSES a non-default value in this section at launch.`;
    }
    if (section.launchSeverity === 'WARN') {
      return `${base} The launch preflight WARNS on a non-default value in this section at launch — this console holds no release-VM seat, so nothing launches from it.`;
    }
    return base;
  }
  if (section.reach === 'release-vm') return 'Rides the launch payload to the release VM.';
  return null;
}

export type SectionsDoc = Record<string, Record<string, unknown>>;

const stable = (v: unknown): string => JSON.stringify(v) ?? 'null';

/** The console's audit-path spelling — the default `pathFor`. */
export const valuesPath = (sectionId: string, key: string): string => `values.${sectionId}.${key}`;
export type PathFor = (sectionId: string, key: string) => string;

/** How many variables a document overrides away from the catalog default.
 * Counted against the CATALOG (an override equal to the default, or naming a
 * retired variable, does not count — same rule as the server's prune). */
export function overrideCount(catalog: CfgSection[], sections: SectionsDoc): number {
  let n = 0;
  for (const s of catalog) {
    for (const v of s.variables) {
      const ov = sections[s.id]?.[v.key];
      if (ov !== undefined && stable(ov) !== stable(v.default)) n++;
    }
  }
  return n;
}

// ---------------------------------------------------------------------------
// Draft state
//
// The draft is a sparse SectionsDoc seeded from the saved doc; every edit
// writes the TYPED value into the draft; save sends prune(draft). "Changed
// from default" and "dirty" are both derived — never tracked as flags that
// could drift from the values.

/** The value the editor shows for a variable: draft beats saved beats default. */
export function shownValue(v: CfgVariable, sectionId: string, saved: SectionsDoc, draft: SectionsDoc): unknown {
  const d = draft[sectionId]?.[v.key];
  if (d !== undefined) return d;
  const s = saved[sectionId]?.[v.key];
  if (s !== undefined) return s;
  return v.default;
}

export function changedFromDefault(v: CfgVariable, value: unknown): boolean {
  return stable(value) !== stable(v.default);
}

/** Set a draft value (returns a new doc; the old one is not mutated). */
export function setDraft(draft: SectionsDoc, sectionId: string, key: string, value: unknown): SectionsDoc {
  return { ...draft, [sectionId]: { ...draft[sectionId], [key]: value } };
}

/** Drop overrides equal to the catalog default — what PATCH sends. Mirrors
 * the server's pruneDefaults so the client's dirty math and the server's
 * stored doc cannot disagree. */
export function sparseFromDraft(catalog: CfgSection[], draft: SectionsDoc): SectionsDoc {
  const out: SectionsDoc = {};
  for (const s of catalog) {
    for (const v of s.variables) {
      const ov = draft[s.id]?.[v.key];
      if (ov === undefined || stable(ov) === stable(v.default)) continue;
      (out[s.id] ??= {})[v.key] = ov;
    }
  }
  return out;
}

export interface PendingChange {
  path: string;
  sectionId: string;
  key: string;
  sectionTitle: string;
  label: string;
  old: unknown;
  new: unknown;
}

/** The review pane's rows: what SAVE will change, old -> new, in catalog
 * order — default-resolved on both sides, the exact semantics the server's
 * diffSections uses for the audit rows, so what the review pane shows IS
 * what the audit trail will say. */
export function pendingDiff(
  catalog: CfgSection[],
  saved: SectionsDoc,
  draft: SectionsDoc,
  pathFor: PathFor = valuesPath,
): PendingChange[] {
  const out: PendingChange[] = [];
  for (const s of catalog) {
    for (const v of s.variables) {
      const so = saved[s.id]?.[v.key];
      const before = so !== undefined ? so : v.default;
      const after = shownValue(v, s.id, saved, draft);
      if (stable(before) !== stable(after)) {
        out.push({
          path: pathFor(s.id, v.key), sectionId: s.id, key: v.key,
          sectionTitle: s.title, label: v.label, old: before, new: after,
        });
      }
    }
  }
  return out;
}

/** Parse a control's raw string into the variable's typed value.
 * Returns { value } or a NAMED { error } the row renders inline — the same
 * refuse-don't-clamp posture as the server, so nothing surprising survives
 * to the save. */
export function parseControlValue(v: CfgVariable, raw: string): { value?: unknown; error?: string } {
  switch (v.type) {
    case 'number': {
      if (raw.trim() === '') return { error: 'a number is required' };
      const n = Number(raw);
      if (!Number.isFinite(n)) return { error: 'not a number' };
      if (v.min !== undefined && n < v.min) return { error: `minimum is ${v.min}` };
      if (v.max !== undefined && n > v.max) return { error: `maximum is ${v.max}` };
      return { value: n };
    }
    case 'json': {
      const t = raw.trim();
      if (t === '') return { error: 'JSON required — use null for "seat absent"' };
      try {
        return { value: JSON.parse(t) };
      } catch {
        return { error: 'not valid JSON' };
      }
    }
    case 'select': {
      if (!v.options?.some((o) => o.value === raw)) return { error: `not one of the ${v.options?.length ?? 0} options` };
      return { value: raw };
    }
    default:
      return { value: raw };
  }
}

/** Render a value for the review pane / default hints. Short, lossless for
 * scalars; JSON values are compacted and elided past 60 chars. */
export function displayValue(v: CfgVariable, value: unknown): string {
  if (v.type === 'toggle') return value ? 'on' : 'off';
  if (v.type === 'select') {
    const o = v.options?.find((x) => x.value === value);
    if (o) return o.label;
  }
  if (v.type === 'text') return value === '' ? '(empty)' : String(value);
  if (v.type === 'number') return String(value);
  const s = stable(value);
  return s.length > 60 ? `${s.slice(0, 57)}…` : s;
}

// ---------------------------------------------------------------------------
// Per-setting reset / number stepper — pure decisions the mount renders.

export interface ResetVm {
  /** false = render NO button: read-only viewers get no write affordance,
   * and a variable the catalog declares no default for has nothing to
   * reset TO (measured 0 of 156 in the shipped catalog — the count is
   * asserted in the headless suite so a future default-less variable is a
   * visible decision, not a silent dangle). */
  present: boolean;
  /** 'atdefault' -> the disabled green check; 'changed' -> the enabled
   * yellow reset. The DEFAULT is the catalog's v.default — the same single
   * authority every other judgment here reads; never a second copy. */
  state: 'atdefault' | 'changed';
  /** aria-label AND data-tip — names the default so the hover says what
   * the click restores. */
  tip: string;
}

export function resetVm(v: CfgVariable, value: unknown, canManage: boolean): ResetVm {
  const state = changedFromDefault(v, value) ? 'changed' : 'atdefault';
  return {
    present: canManage && v.default !== undefined,
    state,
    tip: state === 'atdefault'
      ? 'At the default value'
      : `Reset to default (${displayValue(v, v.default)})`,
  };
}

/** Variables with NO catalog default (resetVm renders no button for these).
 * The suite pins the measured count so the "no button" arm is a stated
 * number, never an invisible class. */
export function noDefaultCount(catalog: CfgSection[]): number {
  let n = 0;
  for (const s of catalog) for (const v of s.variables) if (v.default === undefined) n++;
  return n;
}

/** Which stepper arrows are live for the current value: stepping is a
 * BOUNDED gesture (an arrow at the rail does nothing), unlike typing,
 * which parseControlValue refuses by name rather than clamping. A
 * non-numeric current value leaves both arms live — the first step lands
 * on a real number (stepNumber's re-anchor). */
export function stepperVm(v: CfgVariable, value: unknown): { canUp: boolean; canDown: boolean } {
  const n = typeof value === 'number' ? value : Number(value);
  if (!Number.isFinite(n)) return { canUp: true, canDown: true };
  return {
    canUp: v.max === undefined || n < v.max,
    canDown: v.min === undefined || n > v.min,
  };
}

const decimalsOf = (n: number): number => {
  const s = String(n);
  const i = s.indexOf('.');
  return i === -1 ? 0 : s.length - i - 1;
};

/** One stepper click / arrow key: current raw string -> next raw string.
 * Steps by v.step (default 1), CLAMPS at min/max (the bounded gesture),
 * and rounds to the wider of the step's and the value's decimal places so
 * float artifacts (0.1 + 0.2 = 0.30000000000000004) never reach the input.
 * An unparseable current value re-anchors on the catalog default (or min)
 * instead of stepping from NaN. */
export function stepNumber(v: CfgVariable, raw: string, dir: 1 | -1): string {
  const step = v.step ?? 1;
  const cur = raw.trim() === '' ? NaN : Number(raw);
  if (!Number.isFinite(cur)) {
    const anchor = typeof v.default === 'number' ? v.default : (v.min ?? 0);
    return String(anchor);
  }
  let next = cur + dir * step;
  if (v.min !== undefined && next < v.min) next = v.min;
  if (v.max !== undefined && next > v.max) next = v.max;
  const dec = Math.max(decimalsOf(step), decimalsOf(cur));
  return String(Number(next.toFixed(Math.min(dec, 12))));
}

// ---------------------------------------------------------------------------
// Search + section nav

export interface FilteredSection {
  section: CfgSection;
  variables: CfgVariable[];
}

/** Case-insensitive search across label, key, help, seat and section title.
 * An empty query returns everything; a section-title hit keeps the whole
 * section (you searched for the group). */
export function filterCatalog(catalog: CfgSection[], query: string): FilteredSection[] {
  const q = query.trim().toLowerCase();
  if (!q) return catalog.map((s) => ({ section: s, variables: s.variables }));
  const out: FilteredSection[] = [];
  for (const s of catalog) {
    if (s.title.toLowerCase().includes(q)) {
      out.push({ section: s, variables: s.variables });
      continue;
    }
    const hits = s.variables.filter((v) =>
      `${v.label} ${v.key} ${v.help} ${v.seat}`.toLowerCase().includes(q));
    if (hits.length > 0) out.push({ section: s, variables: hits });
  }
  return out;
}

export interface SectionNavEntry {
  id: string;
  title: string;
  total: number;
  shown: number;
  changed: number;
  dirty: number;
}

/** The sticky nav's rows: per-section totals, how many survive the current
 * search, how many differ from the default, how many have unsaved edits. */
export function sectionNav(
  catalog: CfgSection[],
  saved: SectionsDoc,
  draft: SectionsDoc,
  query: string,
): SectionNavEntry[] {
  const filtered = new Map(filterCatalog(catalog, query).map((f) => [f.section.id, f.variables.length]));
  return catalog.map((s) => {
    let changed = 0;
    let dirty = 0;
    for (const v of s.variables) {
      const val = shownValue(v, s.id, saved, draft);
      if (changedFromDefault(v, val)) changed++;
      const so = saved[s.id]?.[v.key];
      const before = so !== undefined ? so : v.default;
      if (stable(before) !== stable(val)) dirty++;
    }
    return { id: s.id, title: s.title, total: s.variables.length, shown: filtered.get(s.id) ?? 0, changed, dirty };
  });
}
